import type { UniDriver } from '@wix/wix-ui-test-utils/unidriver';

import { baseUniDriverFactory } from '../test-utils/utils/unidriver';
// @ts-ignore
import { textUniDriverFactory } from '../Text/Text.uni.driver';
// @ts-ignore
import { infoIconDriverFactory } from '../InfoIcon/InfoIcon.uni.driver';

import { dataHooks } from './constants';
import {
  LegendAlignment,
  LegendPlacement,
  LegendSize,
  FieldSetStatusType,
  Alignment,
  Direction,
  Gap,
} from './FieldSet.types';

export const fieldSetDriverFactory = (base: UniDriver, body: UniDriver) => {
  const getCharactersCounter = async () =>
    await base.get(`[data-hook*="${dataHooks.counter}"]`);

  return {
    ...baseUniDriverFactory(base),
    /** get legend text */
    getLegendText: async () => {
      const baseUniDriverElement = await base.get(
        `[data-hook*="${dataHooks.label}"]`,
      );
      return baseUniDriverElement.text();
    },
    /** returns legend size */
    getLegendSize: async (): Promise<LegendSize> => {
      const labelDriver = textUniDriverFactory(
        await base.get(`[data-hook="${dataHooks.label}"]`),
      );
      return labelDriver.getSize();
    },
    /** returns legend placement */
    getLegendPlacement: async (): Promise<LegendPlacement> => {
      return (await base.attr('data-legend-placement')) as LegendPlacement;
    },
    /** returns legend alignment */
    getLegendAlignment: async (): Promise<LegendAlignment> => {
      return (await base.attr('data-legend-alignment')) as LegendAlignment;
    },
    /** returns true whether field set is required */
    isRequired: async () =>
      (await base.get(`[data-hook*="${dataHooks.asterisk}"]`)).exists(),
    /** returns the length left */
    getCharCount: async () => {
      const counter = await getCharactersCounter();
      return parseInt(await counter.text(), 10);
    },
    /** returns whether the field set length is exceeded */
    isLengthExceeded: async () => {
      const counter = await getCharactersCounter();
      const length = parseInt(await counter.text(), 10);
      return length < 0;
    },
    /** returns tooltip text of the info content */
    getInfoContent: async (): Promise<string> => {
      const testkit = infoIconDriverFactory(
        await base.get(`[data-hook="${dataHooks.infoIcon}"]`),
        body,
      );
      return testkit.getContent();
    },
    getAlignment: async () => {
      return (await base.attr('data-alignment')) as Alignment;
    },
    getDirection: async () => {
      return (await base.attr('data-direction')) as Direction;
    },
    getGap: async () => {
      return (await base.attr('data-gap')) as Gap;
    },
    /** returns status message text */
    getStatusMessage: async (): Promise<string> => {
      const testkit = textUniDriverFactory(
        base.$(`[data-hook="${dataHooks.statusMessage}"]`),
      );
      return testkit.getText();
    },
    /** return true if it has a given status */
    hasStatus: async (status: FieldSetStatusType) =>
      (await base.attr('data-status')) === status,
  };
};

export type FieldSetUniDriver = ReturnType<typeof fieldSetDriverFactory>;
